<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { apiGet } from "../api";
import Pager from "../components/Pager.vue";
import SortTh from "../components/SortTh.vue";

interface StockItem { id: string; sku: string; name: string; category: string; stockTotal: number; stockState: string }

const router = useRouter();
const items = ref<StockItem[]>([]);
const loading = ref(true);
const error = ref("");
const limit = 50;
const offset = ref(0);
const total = ref(0);
const sort = ref("name");
const order = ref<"asc" | "desc">("asc");
const pct = (s: string) => (s === "out_of_stock" ? 6 : s === "low" ? 35 : 100);

async function load() {
  loading.value = true;
  const qs = new URLSearchParams({ limit: String(limit), offset: String(offset.value), sort: sort.value, order: order.value });
  try {
    const d = await apiGet<{ items: StockItem[]; total: number }>(`/stock?${qs.toString()}`, { auth: true });
    items.value = d.items; total.value = d.total;
  } catch (e) { error.value = String(e); } finally { loading.value = false; }
}
function reload() { if (offset.value !== 0) offset.value = 0; else load(); }
function setSort(col: string) {
  if (sort.value === col) order.value = order.value === "asc" ? "desc" : "asc";
  else { sort.value = col; order.value = "asc"; }
}
watch([sort, order], reload);
watch(offset, load);
onMounted(load);
</script>

<template>
  <div>
    <h1 class="title">Voorraad <span class="count">· {{ total }}</span></h1>
    <p class="lock">🔒 Beveiligd endpoint — geladen met je admin-token</p>
    <p v-if="loading" class="muted">Laden…</p>
    <p v-else-if="error" class="err">Kon voorraad niet laden ({{ error }}). Ben je ingelogd?</p>
    <table v-else class="tbl">
      <thead><tr>
        <SortTh label="Product" col="name" :sort="sort" :order="order" @sort="setSort" />
        <SortTh label="Categorie" col="category" :sort="sort" :order="order" @sort="setSort" />
        <th>Voorraadniveau</th>
        <SortTh label="Aantal" col="stock" :sort="sort" :order="order" @sort="setSort" />
      </tr></thead>
      <tbody>
        <tr v-for="i in items" :key="i.id" class="clickrow" @click="router.push(`/producten/${i.id}/bewerken`)">
          <td><div class="pname">{{ i.name }}</div><div class="psku">{{ i.sku }}</div></td>
          <td>{{ i.category }}</td>
          <td><div class="bar"><div class="fill" :class="i.stockState" :style="{ width: pct(i.stockState) + '%' }" /></div></td>
          <td :class="['amt', i.stockState]">{{ i.stockTotal }}</td>
        </tr>
      </tbody>
    </table>
    <Pager v-if="!loading && !error" v-model:offset="offset" :limit="limit" :total="total" />
  </div>
</template>

<style scoped>
.title { font-family: var(--font-ui); font-weight: 700; font-size: 24px; margin: 0 0 6px; }
.count { color: var(--muted); font-weight: 600; }
.lock { color: var(--muted); font-size: 12px; margin: 0 0 18px; }
.muted { color: var(--muted); }
.err { color: var(--red); }
.tbl { width: 100%; border-collapse: collapse; font-size: 14px; }
.tbl th { text-align: left; color: var(--muted); font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; padding: 8px 12px; border-bottom: 1px solid var(--hairline); }
.tbl td { padding: 14px 12px; border-bottom: 1px solid var(--hairline); vertical-align: middle; }
.clickrow { cursor: pointer; } .clickrow:hover { background: var(--surface-off); }
.pname { font-weight: 600; }
.psku { color: var(--muted); font-size: 12px; font-family: monospace; }
.bar { width: 160px; height: 8px; border-radius: 999px; background: var(--warm); overflow: hidden; }
.fill { height: 100%; border-radius: 999px; }
.fill.in_stock { background: var(--green); }
.fill.low { background: #e9b949; }
.fill.out_of_stock { background: var(--red); }
.amt.low { color: var(--amber); }
.amt.out_of_stock { color: var(--red); }
.amt.in_stock { color: var(--green); }
</style>
